import type { MouseEvent } from "react";
import { SCAN_STAGES } from "../core/primitives/scan-progress";
import {
  notRecognised,
  type PendingCapture,
  pendingCaption,
  pendingTitle,
  type TraySegment,
  traySegments,
} from "../pages/scan-card/scan-tray";

/** Done and running share the accent, the running stage breathing, as iOS's row. */
const SEGMENT: Record<TraySegment, string> = {
  complete: "bg-accent",
  active: "animate-pulse bg-accent motion-reduce:animate-none",
  failed: "bg-danger",
  pending: "bg-surface-strong",
};

/**
 * A card of this browser's scan session still on its way, as iOS's `AdminPendingScanRow`: above the list it lands in,
 * until the list read after it shows the card.
 */
export function ScanningScanRow({
  capture,
  onRetry,
  onDiscard,
}: {
  capture: PendingCapture & { readonly previewUrl: string | null };
  onRetry: () => void;
  onDiscard: () => void;
}) {
  const failed = capture.status === "failed";
  // Retry and Discard leave with their own press: the focus stays on the row, or goes to the next row on its way,
  // else to the page's heading, rather than to the document.
  const retry = (e?: MouseEvent<HTMLButtonElement>) => {
    const self = e?.currentTarget.closest<HTMLElement>("[data-scanning-row]");
    onRetry();
    self?.focus({ preventScroll: true });
  };
  const discard = (e?: MouseEvent<HTMLButtonElement>) => {
    const self = e?.currentTarget.closest<HTMLElement>("[data-scanning-row]") ?? null;
    const rows = [...(self?.parentElement?.querySelectorAll<HTMLElement>("[data-scanning-row]") ?? [])];
    const at = self ? rows.indexOf(self) : -1;
    const next = rows[at + 1] ?? rows[at - 1] ?? self?.closest("main")?.querySelector<HTMLElement>("h1, h2") ?? null;
    onDiscard();
    if (next === null) return;
    if (!next.hasAttribute("tabindex")) next.setAttribute("tabindex", "-1");
    next.focus({ preventScroll: true });
  };
  return (
    <div
      tabIndex={-1}
      data-scanning-row=""
      className="flex items-center gap-3 rounded-row border border-line bg-surface p-3 outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {capture.previewUrl === null ? (
        <span className="flex size-12 shrink-0 items-center justify-center rounded-thumb bg-surface-strong text-[10px] font-semibold text-muted">
          UPC
        </span>
      ) : (
        <img
          src={capture.previewUrl}
          alt=""
          className="size-12 shrink-0 rounded-thumb bg-surface-strong object-cover"
        />
      )}
      <div className="min-w-0 flex-1">
        <p
          className={`text-[15px] font-medium ${failed ? "text-danger" : notRecognised(capture) ? "text-warn" : "text-white"}`}
        >
          {pendingTitle(capture)}
        </p>
        <p className={`mt-1 line-clamp-2 text-xs ${failed ? "text-danger" : "text-muted"}`}>
          {pendingCaption(capture)}
        </p>
        <span aria-hidden="true" className="mt-1.5 flex gap-1.5">
          {traySegments(capture).map((segment, i) => (
            <span key={SCAN_STAGES[i]?.key ?? i} className={`h-1 flex-1 rounded-full ${SEGMENT[segment]}`} />
          ))}
        </span>
      </div>
      {failed && (
        <div className="flex shrink-0 items-center gap-1.5">
          <button type="button" onClick={retry} className="btn-secondary btn-xs">
            Retry
          </button>
          <button type="button" onClick={discard} className="btn-ghost btn-xs text-danger">
            Discard
          </button>
        </div>
      )}
    </div>
  );
}
